import { useEffect, useMemo, useState } from "react";
import { Select, Table } from "antd";
import { useSelector } from "react-redux";
import {
  SUMMARY_COLS,
  buildTimecardSummary,
  filterByDeptTeam,
  uniqueOptions,
} from "./timecardSummary";
import "./TimecardSummaryTable.scss";

/**
 * Monthly summary grid for Listë Prezencat — one row per employee with
 * hour buckets, break deductions, and department/team filters.
 */
function TimecardSummaryTable({ detailRows = [], onFilterChange }) {
  const { isDarkMode } = useSelector((s) => s.usersList);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);

  const allSummary = useMemo(
    () => buildTimecardSummary(detailRows),
    [detailRows],
  );

  const deptOptions = useMemo(
    () => uniqueOptions(allSummary, SUMMARY_COLS.department),
    [allSummary],
  );
  const teamOptions = useMemo(
    () => uniqueOptions(allSummary, SUMMARY_COLS.team),
    [allSummary],
  );

  const filtered = useMemo(
    () => filterByDeptTeam(allSummary, { departments, teams }),
    [allSummary, departments, teams],
  );

  useEffect(() => {
    onFilterChange?.(filtered, { departments, teams });
  }, [filtered, departments, teams, onFilterChange]);

  const columns = useMemo(
    () => [
      {
        title: SUMMARY_COLS.employee,
        dataIndex: SUMMARY_COLS.employee,
        key: "employee",
        sorter: (a, b) =>
          String(a[SUMMARY_COLS.employee]).localeCompare(
            String(b[SUMMARY_COLS.employee]),
            "sq",
          ),
        fixed: "left",
        width: 180,
      },
      {
        title: SUMMARY_COLS.department,
        dataIndex: SUMMARY_COLS.department,
        key: "department",
        width: 140,
        filters: deptOptions.map((d) => ({ text: d, value: d })),
        onFilter: (value, record) => record[SUMMARY_COLS.department] === value,
      },
      {
        title: SUMMARY_COLS.team,
        dataIndex: SUMMARY_COLS.team,
        key: "team",
        width: 140,
        filters: teamOptions.map((t) => ({ text: t, value: t })),
        onFilter: (value, record) => record[SUMMARY_COLS.team] === value,
      },
      {
        title: SUMMARY_COLS.before19,
        dataIndex: SUMMARY_COLS.before19,
        key: "before19",
        align: "right",
        width: 110,
        render: (v) => Number(v || 0).toFixed(2),
      },
      {
        title: SUMMARY_COLS.between19and22,
        dataIndex: SUMMARY_COLS.between19and22,
        key: "between",
        align: "right",
        width: 120,
        render: (v) => Number(v || 0).toFixed(2),
      },
      {
        title: SUMMARY_COLS.after22,
        dataIndex: SUMMARY_COLS.after22,
        key: "after22",
        align: "right",
        width: 120,
        render: (v) => Number(v || 0).toFixed(2),
      },
      {
        title: SUMMARY_COLS.totalHours,
        dataIndex: SUMMARY_COLS.totalHours,
        key: "total",
        align: "right",
        width: 110,
        render: (v) => <strong>{Number(v || 0).toFixed(2)}</strong>,
      },
      {
        title: SUMMARY_COLS.breakHours,
        dataIndex: SUMMARY_COLS.breakHours,
        key: "break",
        align: "right",
        width: 140,
        render: (v) => Number(v || 0).toFixed(2),
      },
      {
        title: SUMMARY_COLS.hoursWithBreak,
        dataIndex: SUMMARY_COLS.hoursWithBreak,
        key: "withBreak",
        align: "right",
        width: 120,
        render: (v) => Number(v || 0).toFixed(2),
      },
    ],
    [deptOptions, teamOptions],
  );

  const totals = useMemo(() => {
    const keys = [
      SUMMARY_COLS.before19,
      SUMMARY_COLS.between19and22,
      SUMMARY_COLS.after22,
      SUMMARY_COLS.totalHours,
      SUMMARY_COLS.breakHours,
      SUMMARY_COLS.hoursWithBreak,
    ];
    const sum = Object.fromEntries(keys.map((k) => [k, 0]));
    filtered.forEach((row) => {
      keys.forEach((k) => {
        sum[k] += Number(row[k]) || 0;
      });
    });
    return Object.fromEntries(
      Object.entries(sum).map(([k, v]) => [k, parseFloat(v.toFixed(2))]),
    );
  }, [filtered]);

  if (!allSummary.length) return null;

  return (
    <div
      className={`timecard-summary ${isDarkMode ? "dark" : ""}`}
      data-testid="timecard-summary"
    >
      <div className="timecard-summary-header">
        <h3>Përmbledhje mujore — kartelat e orarit</h3>
        <div className="timecard-summary-filters">
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtro Departamenti"
            value={departments}
            onChange={setDepartments}
            options={deptOptions.map((d) => ({ label: d, value: d }))}
            style={{ minWidth: 200 }}
            maxTagCount="responsive"
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtro Team / Branch"
            value={teams}
            onChange={setTeams}
            options={teamOptions.map((t) => ({ label: t, value: t }))}
            style={{ minWidth: 200 }}
            maxTagCount="responsive"
          />
        </div>
      </div>
      <Table
        size="small"
        rowKey={(r) => r[SUMMARY_COLS.employee]}
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        scroll={{ x: 1200 }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <strong>TOTALI ({filtered.length})</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} />
              <Table.Summary.Cell index={2} />
              {[
                SUMMARY_COLS.before19,
                SUMMARY_COLS.between19and22,
                SUMMARY_COLS.after22,
                SUMMARY_COLS.totalHours,
                SUMMARY_COLS.breakHours,
                SUMMARY_COLS.hoursWithBreak,
              ].map((k, i) => (
                <Table.Summary.Cell index={i + 3} key={k} align="right">
                  <strong>{Number(totals[k] || 0).toFixed(2)}</strong>
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
}

export default TimecardSummaryTable;
